import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Shield, Mail, Smartphone, Eye, EyeOff, Check, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';

const SecuritySettings = () => {
  const { isAdmin } = useAuth();
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, method: null });
  const [loading, setLoading] = useState(true);
  const [setupMethod, setSetupMethod] = useState(null);
  const [setupStep, setSetupStep] = useState('choose'); // choose, verify
  const [verificationCode, setVerificationCode] = useState('');
  const [totpData, setTotpData] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setTwoFAStatus(res.data);
    } catch (e) {
      console.error('Failed to fetch 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupEmail = async () => {
    setSetupMethod('email');
    setSetupStep('verify');
    setVerificationCode('');
    
    try {
      await api.post('/auth/2fa/setup/email');
      toast.success('Verification code sent to your email');
    } catch (e) {
      toast.error('Failed to send verification code');
      setSetupStep('choose');
    }
  };

  const handleSetupTOTP = async () => {
    setSetupMethod('totp');
    setSetupStep('verify');
    setVerificationCode('');
    
    try {
      const res = await api.post('/auth/2fa/setup/totp');
      setTotpData(res.data);
    } catch (e) {
      toast.error('Failed to start authenticator setup');
      setSetupStep('choose');
    }
  };

  const handleVerifySetup = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    setVerifying(true);
    try {
      const endpoint = setupMethod === 'email' 
        ? '/auth/2fa/setup/email/verify'
        : '/auth/2fa/setup/totp/verify';
      
      const res = await api.post(endpoint, { code: verificationCode.trim() });
      
      toast.success(res.data.message);
      setTwoFAStatus({ enabled: true, method: setupMethod });
      setSetupStep('choose');
      setSetupMethod(null);
      setTotpData(null);
      setVerificationCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable2FA = async () => {
    setDisabling(true);
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      toast.success('Two-factor authentication disabled');
      setTwoFAStatus({ enabled: false, method: null });
      setDisableDialogOpen(false);
      setDisablePassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setDisabling(false);
    }
  };

  const cancelSetup = () => {
    setSetupStep('choose');
    setSetupMethod(null);
    setTotpData(null);
    setVerificationCode('');
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-primary" />
            Security Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-primary" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Manage two-factor authentication for your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Status */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${twoFAStatus.enabled ? 'bg-green-100' : 'bg-slate-200'}`}>
                {twoFAStatus.enabled ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Shield className="w-5 h-5 text-slate-400" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-800">
                  {twoFAStatus.enabled ? 'Two-factor authentication is enabled' : 'Two-factor authentication is disabled'}
                </p>
                {twoFAStatus.enabled && (
                  <p className="text-sm text-slate-500">
                    Using {twoFAStatus.method === 'email' ? 'email verification' : 'authenticator app'}
                  </p>
                )}
              </div>
            </div>
            {twoFAStatus.enabled && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setDisableDialogOpen(true)}
                data-testid="disable-2fa-btn"
              >
                Disable
              </Button>
            )}
          </div>

          {/* Setup Section - only show if not enabled */}
          {!twoFAStatus.enabled && setupStep === 'choose' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Add an extra layer of security to your account. Choose your preferred verification method:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <button
                  onClick={handleSetupEmail}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-slate-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-colors text-left"
                  data-testid="setup-email-2fa-btn"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-slate-800">Email Verification</p>
                    <p className="text-xs text-slate-500 mt-1">Receive a code via email each time you sign in</p>
                  </div>
                </button>

                {isAdmin && (
                <button
                  onClick={handleSetupTOTP}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-slate-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-colors text-left"
                  data-testid="setup-totp-2fa-btn"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-primary" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-slate-800">Authenticator App</p>
                    <p className="text-xs text-slate-500 mt-1">Use Google Authenticator, Authy, or similar</p>
                  </div>
                </button>
                )}
              </div>
            </div>
          )}

          {/* Verification Step */}
          {!twoFAStatus.enabled && setupStep === 'verify' && (
            <div className="space-y-4">
              {setupMethod === 'totp' && totpData && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Scan this QR code with your authenticator app, then enter the 6-digit code below:
                  </p>
                  <div className="flex justify-center">
                    <img 
                      src={totpData.qr_code} 
                      alt="QR Code for authenticator" 
                      className="w-48 h-48 border border-slate-200 rounded-lg"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 mb-1">Can't scan? Enter this code manually:</p>
                    <code className="text-sm bg-slate-100 px-3 py-1 rounded font-mono">
                      {totpData.manual_entry_key}
                    </code>
                  </div>
                </div>
              )}

              {setupMethod === 'email' && (
                <p className="text-sm text-slate-600">
                  We've sent a verification code to your email. Enter it below to enable email-based two-factor authentication.
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="verify-code">Verification Code</Label>
                <Input
                  id="verify-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-xl tracking-widest font-mono"
                  data-testid="setup-2fa-code-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={cancelSetup}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleVerifySetup}
                  disabled={verifying || verificationCode.length < 6}
                  className="flex-1 bg-primary"
                  data-testid="verify-setup-code-btn"
                >
                  {verifying ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Verify & Enable
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disable 2FA Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password to confirm disabling 2FA. This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable-password">Password</Label>
              <div className="relative">
                <Input
                  id="disable-password"
                  type={showDisablePassword ? 'text' : 'password'}
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowDisablePassword(!showDisablePassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showDisablePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={disabling || !disablePassword}
            >
              {disabling ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SecuritySettings;
